import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, Chip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import db from '../../data/demoDatabase';
import { PRODUCT_CATEGORIES } from '../../data/productConfigData';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';

// Format a commission entry into a display rate + min/max fields
function formatCommission(commission) {
  if (!commission) return { type: '-', rate: '-', minAmount: '-', maxAmount: '-' };
  if (commission.commissionType === 'per_case') {
    return {
      type: 'Flat',
      rate: `RM ${Number(commission.perCaseAmount || 0).toLocaleString()}`,
      minAmount: '-',
      maxAmount: '-',
    };
  }
  // percentage with tiers
  const tiers = Array.isArray(commission.tiers) ? commission.tiers : [];
  if (tiers.length === 0) {
    return { type: 'Percentage', rate: '-', minAmount: '-', maxAmount: '-' };
  }
  // Show the first tier's rate as headline; list the range as min/max across tiers
  const rates = tiers.map((t) => `${t.rate}%`).join(' / ');
  const min = tiers[0]?.min ?? 0;
  const last = tiers[tiers.length - 1];
  const max = last?.max;
  return {
    type: 'Percentage',
    rate: rates,
    minAmount: min > 0 ? `RM ${Number(min).toLocaleString()}` : 'RM 0',
    maxAmount: max ? `RM ${Number(max).toLocaleString()}` : 'No cap',
  };
}

export default function CommissionTable() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [refreshKey] = useState(0);

  // Load active product configs
  const activeConfigs = useMemo(() => {
    return db.productConfigs
      .getAll()
      .filter((pc) => pc.computedStatus === 'active' || pc.approvalStatus === 'active');
    // eslint-disable-next-line
  }, [user, refreshKey]);

  // Group by category
  const configsByCategory = useMemo(() => {
    const map = {};
    PRODUCT_CATEGORIES.forEach((cat) => {
      map[cat.key] = [];
    });
    activeConfigs.forEach((pc) => {
      if (!map[pc.category]) map[pc.category] = [];
      map[pc.category].push(pc);
    });
    return map;
  }, [activeConfigs]);

  const currentCategory = PRODUCT_CATEGORIES[activeTab];
  const categoryConfigs = configsByCategory[currentCategory?.key] || [];

  // Flatten each config's commissions into table rows
  const rows = useMemo(() => {
    const out = [];
    categoryConfigs.forEach((pc) => {
      const activeCommissions = (pc.commissions || []).filter(
        (c) => c.approvalStatus === 'active' && !c.effectiveTo,
      );
      if (activeCommissions.length === 0) {
        out.push({
          key: `${pc.id}-none`,
          lender: pc.lenderName,
          product: pc.productName,
          recipient: '-',
          type: '-',
          rate: '-',
          minAmount: '-',
          maxAmount: '-',
        });
        return;
      }
      activeCommissions.forEach((c, idx) => {
        const fmt = formatCommission(c);
        out.push({
          key: `${pc.id}-${c.id || idx}`,
          lender: pc.lenderName,
          product: pc.productName,
          recipient: c.recipientName || '-',
          type: fmt.type,
          rate: fmt.rate,
          minAmount: fmt.minAmount,
          maxAmount: fmt.maxAmount,
        });
      });
    });
    return out;
  }, [categoryConfigs]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h4" sx={{ fontWeight: 700, color: PRIMARY, mb: 0.5 }}>
        Commission Rates
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View commission rates for all products
      </Typography>

      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            fontWeight: 600, textTransform: 'none', fontSize: '0.95rem',
            minWidth: 'auto', px: 3,
          },
          '& .Mui-selected': { color: PRIMARY },
          '& .MuiTabs-indicator': { backgroundColor: PRIMARY },
        }}
      >
        {PRODUCT_CATEGORIES.map((cat) => (
          <Tab key={cat.key} label={cat.label} />
        ))}
      </Tabs>

      {/* Commission Table */}
      {rows.length > 0 ? (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: PRIMARY }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Lender</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Product Name</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Recipient</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Commission Type</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Rate / Amount</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Min</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Max</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.key}
                  sx={{
                    bgcolor: index % 2 === 0 ? '#FAFBFC' : '#fff',
                    '&:hover': { bgcolor: '#F0F4F8' },
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{row.lender}</TableCell>
                  <TableCell>{row.product}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.recipient}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.type !== '-' ? (
                      <Chip
                        label={row.type}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: row.type === 'Percentage' ? '#E3F2FD' : '#F3E5F5',
                          color: row.type === 'Percentage' ? '#1565C0' : '#7B1FA2',
                        }}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>{row.rate}</TableCell>
                  <TableCell>{row.minAmount}</TableCell>
                  <TableCell>{row.maxAmount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2, mb: 4 }}>No data yet</Alert>
      )}

      {/* Info Alert */}
      <Alert
        severity="info"
        icon={<InfoIcon />}
        sx={{ borderRadius: 2, '& .MuiAlert-message': { fontWeight: 500 } }}
      >
        Commission rates are subject to change. Contact your account manager for details.
      </Alert>
    </Box>
  );
}
